import { Building2, Users, Target, Award, Globe, Briefcase, ShieldCheck, Zap, ArrowLeft, ArrowRight, Mail, Phone, MapPin } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../config/company';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

export default function AboutUs() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const navigate = useNavigate();

  const platforms = [
    {
      id: 'cloudgm',
      name: 'CloudGM',
      tagline: isAr ? 'منصة الخدمات الحكومية والتجارية' : 'Government & Commercial Services Platform',
      description: isAr 
        ? 'منصة شاملة تقدم خدمات حكومية متكاملة للأفراد والعائلات في دولة الإمارات العربية المتحدة. نساعدك في إنجاز معاملاتك بسرعة واحترافية.'
        : 'A comprehensive platform providing integrated government services for individuals and families in the UAE. We help you complete your transactions quickly and professionally.',
      color: 'from-[#3B82F6] to-[#1D4ED8]',
      features: isAr ? [
        'خدمات التأشيرات والإقامة',
        'تخليص المعاملات الحكومية',
        'ترجمة المستندات القانونية',
        'تأسيس الشركات',
        'سوق الأرقام واللوحات المميزة',
        'العقارات والمنتجات'
      ] : [
        'Visa & Residency Services',
        'Government Transaction Processing',
        'Legal Document Translation',
        'Company Formation',
        'Premium Numbers & Plates Market',
        'Real Estate & Products'
      ],
      stats: [
        { value: '10,000+', label: isAr ? 'عميل سعيد' : 'Happy Clients' },
        { value: '50,000+', label: isAr ? 'معاملة منجزة' : 'Completed Transactions' },
        { value: '99%', label: isAr ? 'رضا العملاء' : 'Customer Satisfaction' }
      ]
    },
    {
      id: 'cloudgm-business',
      name: 'CloudGM Business',
      tagline: isAr ? 'منصة الأعمال والتجار' : 'Business & Merchant Platform',
      description: isAr 
        ? 'منصة متخصصة للتجار وأصحاب الأعمال لعرض منتجاتهم وخدماتهم والوصول إلى قاعدة عملاء واسعة. نوفر لك الأدوات اللازمة لتنمية أعمالك.'
        : 'A specialized platform for merchants and business owners to display their products and services and reach a wide customer base.',
      color: 'from-[#F97316] to-[#EA580C]',
      features: isAr ? [
        'إنشاء متجر إلكتروني',
        'إدارة المنتجات والمخزون',
        'معالجة الطلبات',
        'تقارير المبيعات والتحليلات',
        'التواصل مع العملاء',
        'حلول الدفع المتكاملة'
      ] : [
        'Create Online Store',
        'Product & Inventory Management',
        'Order Processing',
        'Sales Reports & Analytics',
        'Customer Communication',
        'Integrated Payment Solutions'
      ],
      stats: [
        { value: '500+', label: isAr ? 'تاجر نشط' : 'Active Merchants' },
        { value: '25,000+', label: isAr ? 'منتج معروض' : 'Listed Products' },
        { value: '100K+', label: isAr ? 'طلب شهري' : 'Monthly Orders' }
      ]
    }
  ];

  const values = [
    {
      icon: <ShieldCheck className="h-8 w-8" />,
      title: isAr ? 'الموثوقية' : 'Reliability',
      description: isAr 
        ? 'نلتزم بأعلى معايير الجودة والدقة في جميع خدماتنا'
        : 'We commit to the highest standards of quality and accuracy in all our services'
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: isAr ? 'السرعة' : 'Speed',
      description: isAr 
        ? 'نحرص على إنجاز معاملاتك في أسرع وقت ممكن'
        : 'We ensure completing your transactions as quickly as possible'
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: isAr ? 'العميل أولاً' : 'Customer First',
      description: isAr 
        ? 'رضا العملاء هو أولويتنا القصوى في كل ما نقدمه'
        : 'Customer satisfaction is our top priority in everything we offer'
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: isAr ? 'الشفافية' : 'Transparency',
      description: isAr 
        ? 'نتعامل بشفافية تامة في الأسعار والإجراءات'
        : 'We operate with complete transparency in pricing and procedures'
    }
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero Section - Compact */}
      <section className={`py-10 ${isDarkMode ? 'bg-gradient-to-r from-[#1E9BD9]/20 to-[#0369A1]/10' : 'bg-gradient-to-r from-[#1E9BD9] to-[#0369A1]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-white'}`} data-testid="about-title">
            {t('pages.aboutTitle')}
          </h1>
          <p className={`text-base max-w-2xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>
            {isAr 
              ? 'CloudGM FZ-LLC - شركة رائدة في تقديم الخدمات الحكومية والتجارية'
              : 'CloudGM FZ-LLC - A leading company in government and commercial services'}
          </p>
        </div>
      </section>

      {/* Company Info */}
      <section className={`py-12 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-2xl p-8 md:p-12 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white shadow-lg'}`}>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {isAr ? 'من نحن' : 'Who We Are'}
                </h2>
                <p className={`mb-6 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {isAr 
                    ? 'CloudGM FZ-LLC هي شركة إماراتية متخصصة في تقديم الحلول المتكاملة للأفراد والشركات. تأسست الشركة برؤية واضحة لتسهيل إجراءات المعاملات الحكومية وتوفير منصة تجارية موثوقة تربط بين التجار والعملاء.'
                    : 'CloudGM FZ-LLC is a UAE company specialized in providing integrated solutions for individuals and businesses. The company was founded with a clear vision to simplify government transaction procedures and provide a reliable commercial platform connecting merchants and customers.'}
                </p>
                <p className="text-[#475569] leading-relaxed">
                  {isAr 
                    ? 'نفخر بكوننا جزءاً من منظومة الأعمال في إمارة رأس الخيمة، ونسعى دائماً لتقديم أفضل الخدمات بأعلى معايير الجودة والاحترافية.'
                    : 'We are proud to be part of the business ecosystem in Ras Al Khaimah, and we always strive to provide the best services with the highest standards of quality and professionalism.'}
                </p>
              </div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1E9BD9]/10 rounded-xl">
                    <Mail className="h-6 w-6 text-[#1E9BD9]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">{t('common.email')}</p>
                    <p className="text-[#0F172A] font-medium">{COMPANY_INFO.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1E9BD9]/10 rounded-xl">
                    <Phone className="h-6 w-6 text-[#1E9BD9]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">{t('common.phone')}</p>
                    <p className="text-[#0F172A] font-medium" dir="ltr">{COMPANY_INFO.phoneDisplay}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1E9BD9]/10 rounded-xl">
                    <MapPin className="h-6 w-6 text-[#1E9BD9]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">{t('common.address')}</p>
                    <p className="text-[#0F172A] font-medium">
                      {isAr ? COMPANY_INFO.address.full : COMPANY_INFO.address.fullEn}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12">
            {isAr ? 'قيمنا' : 'Our Values'}
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#1E9BD9]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <div className="text-[#1E9BD9]">{value.icon}</div>
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{value.title}</h3>
                <p className="text-sm text-[#475569]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Platforms */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-4">
            {isAr ? 'منصاتنا' : 'Our Platforms'}
          </h2>
          <p className="text-center text-[#64748B] mb-12 max-w-2xl mx-auto">
            {isAr 
              ? 'نقدم منصتين متكاملتين لخدمة جميع احتياجاتك'
              : 'We offer two integrated platforms to serve all your needs'}
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {platforms.map((platform) => (
              <div 
                key={platform.id}
                className="bg-white rounded-3xl shadow-lg overflow-hidden"
              >
                <div className={`bg-gradient-to-r ${platform.color} p-8 text-white`}>
                  <h3 className="text-2xl font-bold mb-2">{platform.name}</h3>
                  <p className="opacity-90">{platform.tagline}</p>
                </div>
                <div className="p-8">
                  <p className="text-[#475569] mb-6">{platform.description}</p>
                  <ul className="space-y-3 mb-6">
                    {platform.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-[#475569]">
                        <div className="w-2 h-2 bg-[#1E9BD9] rounded-full"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="grid grid-cols-3 gap-4 pt-6 border-t">
                    {platform.stats.map((stat, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-xl font-bold text-[#1E9BD9]">{stat.value}</div>
                        <div className="text-xs text-[#64748B]">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#1E9BD9] to-[#0369A1] rounded-3xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">
              {isAr ? 'ابدأ رحلتك معنا اليوم' : 'Start Your Journey With Us Today'}
            </h2>
            <p className="mb-8 opacity-90">
              {isAr 
                ? 'سواء كنت فرداً يبحث عن خدمات حكومية أو تاجراً يسعى لتوسيع نطاق أعماله، نحن هنا لمساعدتك'
                : 'Whether you are an individual looking for government services or a merchant seeking to expand your business, we are here to help'}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-white text-[#1E9BD9] hover:bg-gray-100">
                  {t('nav.register')}
                </Button>
              </Link>
              <a href={`mailto:${COMPANY_INFO.email}`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  {t('footer.contact')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
